import UIAccordion from "components/Others/UIAccordion";
import UITabs, { UITab } from "components/Others/UITabs";
import { ProductDetailProps, ProductDetailType } from "constants/dataTypes";
import { getCategoryBasedSectionList } from "functions/notion";
import { stringToSlug } from "functions/slug";
import useDeviceType from "hooks/useDeviceType";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { useGlassesStore } from "zus/glassesStore";
import { useState, useEffect } from 'react';
import { changeSunglasses } from "functions/changeSunglasses";

const FramesContent = dynamic<ProductDetailProps>(() => import('components/ProductDetail/ProductDetail').then((component) => component.default.FramesContent))
const SizingContent = dynamic<ProductDetailProps>(() => import('components/ProductDetail/ProductDetail').then((component) => component.default.SizingContent))
const LensesContent = dynamic<ProductDetailProps>(() => import('components/ProductDetail/ProductDetail').then((component) => component.default.LensesContent))
const LensesContentDresdengo = dynamic<ProductDetailProps>(() => import('components/ProductDetail/ProductDetail').then((component) => component.default.LensesContentDresdengo))
const WhatsInTheBoxContent = dynamic<ProductDetailProps>(() => import('components/ProductDetail/ProductDetail').then((component) => component.default.WhatsInTheBoxContent))
const WhatsInTheBoxGoContentBlueLight = dynamic<ProductDetailProps>(() => import('components/ProductDetail/ProductDetail').then((component) => component.default.WhatsInTheBoxGoContentBlueLight));
const WhatsInTheBoxGoContentReadingGlasses = dynamic<ProductDetailProps>(() => import('components/ProductDetail/ProductDetail').then((component) => component.default.WhatsInTheBoxGoContentReadingGlasses));
const WhatsInTheBoxGoContentNonRxSunglasses = dynamic<ProductDetailProps>(() => import('components/ProductDetail/ProductDetail').then((component) => component.default.WhatsInTheBoxGoContentNonRxSunglasses))
const ShippingAndReturn = dynamic<ProductDetailProps>(() => import('components/ProductDetail/ProductDetail').then((component) => component.default.ShippingAndReturn))

const CustomizeContent = dynamic<ProductDetailProps>(() => import('components/ProductDetail/ProductDetail').then((component) => component.default.CustomizeContent))
const ColorInspirationContent = dynamic<ProductDetailProps>(() => import('components/ProductDetail/ProductDetail').then((component) => component.default.ColorInspirationContent))


export function ProductDetailTabsComponent({ categorySlug }: { categorySlug?: string }) {
    const router = useRouter();
    const [productDetailMap, setProductDetailMap] = useState<ProductDetailType[]>();
    const [isLoading, setIsLoading] = useState(true);

    const category = categorySlug || (router.query.category == 'sunglasses' ? 'non-prescription-sunglasses' : router.query.category) as string;

    useEffect(() => {
        async function fetchProductDetailMap() {
            setIsLoading(true);
            try {
                const result = await getCategoryBasedSectionList(category, router.locale!);
                setProductDetailMap(result);
            } catch (error) {
                console.error('Error fetching product detail map:', error);
            } finally {
                setIsLoading(false);
            }
        }
        fetchProductDetailMap();
    }, [category, router.locale]);


    const frameColorZ = router.query.product as string;
    const frameSizeZ = useGlassesStore((state) => state.frame_size);
    const deviceType = useDeviceType();
    const productFramesContent = useMemo(() => {
        if (productDetailMap != null) {

            const productFrameContent = productDetailMap!.filter((productDetail: any) => {
                return productDetail.slug === `frames-${stringToSlug(frameColorZ)}`
            });
            if (productFrameContent.length > 0) {
                return { description: productFrameContent[0].description, title: productFrameContent[0].sectionTitle }
            }
            const productGroupFrameContent = productDetailMap!.filter((productDetail: any) => {
                return productDetail.slug === `frames`
            });
            if (productGroupFrameContent.length > 0) {
                return { description: productGroupFrameContent[0].description, title: productGroupFrameContent[0].sectionTitle }
            }
            return null;
        }
        return null;
    }, [frameColorZ, productDetailMap]);

    const isBlueLightFilterGlasses = router.asPath.includes('blue-light-filter-glasses');
    const isReadingGlasses = router.asPath.includes('reading-glasses');
    const isNonPrescriptionSunglasses = router.asPath.includes('/sunglasses/');

    const productColourInspiration = useMemo(() => {
        if (productDetailMap != null) {
            const colourInspo = productDetailMap!.filter((productDetail: any) => {
                return productDetail.slug === `color-inspiration-${stringToSlug(frameColorZ)}`
            });
            if (colourInspo.length > 0) {
                return { description: colourInspo[0].description, title: colourInspo[0].sectionTitle == '' ? ' ' : colourInspo[0].sectionTitle }
            }
            return null;
        }
        return null;
    }, [frameColorZ, productDetailMap]);

    const productLensesContent = useMemo(() => {
        if (productDetailMap != null) {
            const productLensContent = productDetailMap!.filter((productDetail: any) => {
                return productDetail.slug === `lenses-${stringToSlug(frameColorZ)}`
            });
            if (productLensContent.length > 0) {
                return productLensContent[0].description
            }
            const productGroupLensContent = productDetailMap!.filter((productDetail: any) => {
                return productDetail.slug === `lenses`
            });
            if (productGroupLensContent.length > 0) {
                return productGroupLensContent[0].description
            }
            return null;
        }
        return null;
    }, [frameColorZ, productDetailMap]);

    const isLensActive: any = useMemo(() => {
        return !router.asPath.includes('frames');
    }, [router]);

    const renderWhatsInTheBox = () => {
        if (isBlueLightFilterGlasses) {
            return <WhatsInTheBoxGoContentBlueLight deviceType={deviceType} />;
        }
        if (isReadingGlasses) {
            return <WhatsInTheBoxGoContentReadingGlasses deviceType={deviceType} />;
        }
        if (isNonPrescriptionSunglasses) {
            return <WhatsInTheBoxGoContentNonRxSunglasses deviceType={deviceType} />;
        }

        return <WhatsInTheBoxContent deviceType={deviceType} />;
    }

    const getLensesContent = () => {
        // if(isReadingGlasses || (isBlueLightFilterGlasses && isNotCanadian)|| (isNonPrescriptionSunglasses && isNotCanadian)) {
        //     return <LensesContentDresdengo deviceType={deviceType} content={productLensesContent!} locale={router.locale} />
        // }

        return <LensesContent deviceType={deviceType} content={productLensesContent!} locale={router.locale} />
    }

    const renderLensesContentTab = (isDesktop: boolean) => {

        if (isLensActive) {
            if (isDesktop) {
                return (
                    <UITab wrapperClassName="min-h-[349px]" title="Lenses">
                        {getLensesContent()}
                    </UITab>
                );
            }
            else if (!isDesktop) {
                return (
                    <UIAccordion title="Lenses">
                        {getLensesContent()}
                    </UIAccordion>
                );
            }
        }

        return <></>;
    }

    const isGlasses = useMemo(() => {
        return router.asPath.includes('glasses');
    }, [router]);




    return (
        <>
            <UITabs wrapperClassName='mt-[50px]' switchToFirstElement={productColourInspiration === null}>
                <UITab wrapperClassName={'min-h-[349px]'} title="Frames">
                    {isLoading ? <div>Loading...</div> : (
                        <FramesContent deviceType={deviceType} content={productFramesContent?.description!} title={productColourInspiration ? productColourInspiration.title : productFramesContent?.title} />
                    )}
                </UITab>
                <UITab wrapperClassName={'h-[479px]'} title="Fit & Sizing">
                    <SizingContent deviceType={deviceType} frameSize={frameSizeZ} />
                </UITab>
                {renderLensesContentTab(true)}
                <UITab wrapperClassName="min-h-[349px]" title="What's in the Box">
                    {renderWhatsInTheBox()}
                </UITab>
                <UITab wrapperClassName={'min-h-[349px]'} title={router.locale === "ca" ? "Customize" : "Customise"}>
                    <CustomizeContent deviceType={deviceType} locale={router.locale} />
                </UITab>

                <UITab wrapperClassName={`h-[230px]`} title={`${productColourInspiration == null ? '' : "Colour Inspiration"}`}>
                    <ColorInspirationContent deviceType={deviceType} title={productColourInspiration ? productColourInspiration.title : ''} content={productColourInspiration ? productColourInspiration.description : ''} />
                </UITab>

                {isGlasses ? (
                    <UITab wrapperClassName={`min-h-[230px]`} title="Shipping and Returns">
                        <ShippingAndReturn deviceType={deviceType} locale={router.locale} path={router.asPath} />
                    </UITab>
                ) : <></>}

            </UITabs>
            <div className="lg:hidden block">
                <UIAccordion title={productColourInspiration ? productColourInspiration.title : 'Frames'} defaultOpen={true}>
                    <FramesContent deviceType={deviceType} content={productFramesContent?.description!} title={productColourInspiration ? productColourInspiration.title : productFramesContent?.title} />
                </UIAccordion>
                <UIAccordion title="Fit & Sizing">
                    <SizingContent deviceType={deviceType} frameSize={frameSizeZ} />
                </UIAccordion>
                {renderLensesContentTab(false)}
                <UIAccordion title="What's in the Box">
                    {renderWhatsInTheBox()}
                </UIAccordion>
                <UIAccordion title={router.locale === "ca" ? "Customize" : "Customise"}>
                    <CustomizeContent deviceType={deviceType} />
                </UIAccordion>
                <>
                    {productColourInspiration !== null && (
                        <UIAccordion title="Color Inspiration">
                            <ColorInspirationContent deviceType={deviceType} title={productColourInspiration ? productColourInspiration.title : ''} content={productColourInspiration ? productColourInspiration.description : ''} />
                        </UIAccordion>
                    )}
                </>

                {isGlasses && (
                    <UIAccordion title="Shipping and Returns">
                        <ShippingAndReturn deviceType={deviceType} locale={router.locale} path={router.asPath} />
                    </UIAccordion>
                )}
            </div>
        </>
    )
}